import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { caseStudies, services } from "@/lib/data";
import { FinalCTA } from "@/components/marketing/Sections";
import Breadcrumbs from "@/components/marketing/Breadcrumbs";
import SEO, { breadcrumbLd } from "@/components/SEO";
import { ArrowUpRight, Check, Quote } from "lucide-react";

export default function CaseStudyDetail() {
    const { slug } = useParams();
    const idx = caseStudies.findIndex((c) => c.slug === slug);
    const cs = idx >= 0 ? caseStudies[idx] : null;
    if (!cs) return <Navigate to="/projects" replace />;
    const csNumber = String(idx + 1).padStart(2, "0");
    const related = (cs.relatedServices || []).map((s) => services.find((sv) => sv.slug === s)).filter(Boolean);
    const url = typeof window !== "undefined" ? window.location.href : `/projects/${slug}`;

    const projectLd = {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        "name": `${cs.client} — ${cs.title}`,
        "about": cs.overview,
        "locationCreated": { "@type": "Place", "name": cs.location },
        "provider": { "@type": "ProfessionalService", "name": "Final Cut Technologies" },
        "url": url,
    };

    return (
        <MarketingLayout>
            <SEO
                title={cs.seo.title}
                description={cs.seo.description}
                ogImage={cs.cover}
                jsonLd={[
                    projectLd,
                    breadcrumbLd([
                        { name: "Home", href: "/" },
                        { name: "Projects", href: "/projects" },
                        { name: cs.client, href: `/projects/${cs.slug}` },
                    ]),
                ]}
            />

            {/* Hero */}
            <section className="pt-[132px] pb-10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Projects", href: "/projects" }, { name: cs.client, href: `/projects/${cs.slug}` }]} />
                    <div className="grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-7">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="w-6 h-px bg-gold" />
                                <span className="eyebrow"><span className="text-gold">●</span> Case Study · {csNumber}</span>
                            </div>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
                                className="font-display text-5xl md:text-[80px] leading-[0.9] tracking-tight"
                            >
                                {cs.client}
                            </motion.h1>
                            <p className="mt-4 text-lg md:text-xl text-ink/70 max-w-2xl">{cs.title}</p>
                        </div>
                        <div className="md:col-span-5 md:border-l md:border-line md:pl-8 md:pt-3">
                            <dl className="grid grid-cols-2 gap-6 text-sm">
                                <div>
                                    <dt className="tick text-muted mb-2">Client</dt>
                                    <dd className="font-display text-lg">{cs.client}</dd>
                                </div>
                                <div>
                                    <dt className="tick text-muted mb-2">Industry</dt>
                                    <dd className="font-display text-lg">{cs.industry}</dd>
                                </div>
                                <div>
                                    <dt className="tick text-muted mb-2">Location</dt>
                                    <dd className="font-display text-lg">{cs.location}</dd>
                                </div>
                                <div>
                                    <dt className="tick text-muted mb-2">Year</dt>
                                    <dd className="font-display text-lg">{cs.year}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cover image */}
            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 pb-10">
                    <motion.div
                        initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
                        whileInView={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative aspect-[16/9] overflow-hidden bg-ink"
                    >
                        <img src={cs.cover} alt={`${cs.client} — ${cs.title}`} loading="eager" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-6 left-6 w-8 h-8 border-t border-l border-bg/80" />
                            <div className="absolute top-6 right-6 w-8 h-8 border-t border-r border-bg/80" />
                            <div className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-bg/80" />
                            <div className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-bg/80" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Overview */}
            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <p className="eyebrow mb-4">Overview</p>
                    </div>
                    <p className="md:col-span-8 font-display text-2xl md:text-4xl leading-[1.15] tracking-tight text-ink">{cs.overview}</p>
                </div>
            </section>

            {/* Challenge / Solution */}
            <section className="border-b border-line bg-bg-alt">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-2 gap-10">
                    <div>
                        <p className="eyebrow mb-4">The Challenge</p>
                        <p className="text-ink/80 leading-relaxed">{cs.challenge}</p>
                    </div>
                    <div>
                        <p className="eyebrow mb-4">Our Solution</p>
                        <p className="text-ink/80 leading-relaxed">{cs.solution}</p>
                    </div>
                </div>
            </section>

            {/* Services + Highlights */}
            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <p className="eyebrow mb-4">Services Provided</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] mb-8">Scope delivered.</h2>
                        <ul className="border-t border-line">
                            {cs.services.map((s, i) => (
                                <li key={s} className="flex items-start gap-4 py-4 border-b border-line">
                                    <span className="font-mono text-xs text-muted w-8 pt-1">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-ink-2">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-6">
                        <p className="eyebrow mb-4">Project Highlights</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] mb-8">What we shipped.</h2>
                        <ul className="grid grid-cols-1 gap-3">
                            {cs.highlights.map((h) => (
                                <li key={h} className="flex items-center gap-3 border border-line px-4 py-3">
                                    <Check className="w-4 h-4 text-gold flex-none" />
                                    <span className="text-sm text-ink-2">{h}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Testimonial (optional) */}
            {cs.testimonial && (
                <section className="bg-panel text-bg grain grain-dark border-b border-line-2">
                    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
                        <Quote className="w-10 h-10 text-gold mb-8" />
                        <blockquote className="font-display text-2xl md:text-4xl leading-[1.25] tracking-tight text-bg">
                            "{cs.testimonial.quote}"
                        </blockquote>
                        <div className="mt-10 flex items-center gap-3">
                            <span className="w-6 h-px bg-gold" />
                            <span className="eyebrow text-bg/60">{cs.testimonial.attribution}</span>
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery */}
            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="flex items-end justify-between gap-6 mb-10">
                        <div>
                            <p className="eyebrow mb-4">Gallery</p>
                            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">On-site work.</h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {cs.gallery.map((g, i) => (
                            <motion.figure
                                key={g.src}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="group"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-ink">
                                    <img src={g.src} alt={g.caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                                </div>
                                <figcaption className="mt-4 text-sm text-muted leading-relaxed">
                                    <span className="tick text-ink/70 mr-2">Photo {String(i + 1).padStart(2, "0")}</span>
                                    {g.caption}
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcome */}
            <section className="border-b border-line bg-bg-alt">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <p className="eyebrow mb-6">Outcome</p>
                    <p className="font-display text-2xl md:text-4xl leading-[1.2] tracking-tight text-ink">{cs.outcome}</p>
                </div>
            </section>

            {/* Technologies installed (optional) */}
            {Array.isArray(cs.technologies) && cs.technologies.length > 0 && (
                <section className="border-b border-line">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="eyebrow mb-4">Technologies installed</p>
                            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">The stack.</h2>
                        </div>
                        <ul className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3 self-end">
                            {cs.technologies.map((t, i) => (
                                <li key={t} className="flex items-center gap-3 border border-line px-4 py-3">
                                    <span className="font-mono text-[10px] tracking-widest text-muted w-8">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="w-1.5 h-1.5 bg-gold shrink-0" />
                                    <span className="text-sm text-ink-2">{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* Client relationship (optional) */}
            {cs.clientRelationship && (
                <section className="bg-panel text-bg grain grain-dark border-b border-line-2">
                    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-4">
                            <p className="eyebrow text-bg/50 mb-4"><span className="text-gold">●</span> Client relationship</p>
                            <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[0.95] text-bg">{cs.clientRelationship.label}</h3>
                        </div>
                        <p className="md:col-span-7 md:col-start-6 text-bg/80 leading-relaxed">{cs.clientRelationship.copy}</p>
                    </div>
                </section>
            )}

            {/* Related services */}
            {related.length > 0 && (
                <section className="border-b border-line">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                        <div className="flex items-end justify-between gap-6 mb-10">
                            <div>
                                <p className="eyebrow mb-4">Related services</p>
                                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">Book the same scope for your space.</h2>
                            </div>
                            <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm link-underline">All services <ArrowUpRight className="w-4 h-4" /></Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {related.map((rs) => (
                                <Link key={rs.slug} to={`/services/${rs.slug}`} className="group border border-line p-6 hover:bg-ink hover:text-bg transition-colors duration-300">
                                    <div className="flex items-center justify-between mb-8">
                                        <span className="font-mono text-xs tracking-widest text-muted group-hover:text-bg/70">{rs.number}</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </div>
                                    <h3 className="font-display text-xl md:text-2xl tracking-tight mb-2">{rs.title}</h3>
                                    <p className="text-sm text-muted group-hover:text-bg/70">{rs.short}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <FinalCTA />
        </MarketingLayout>
    );
}
